import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { safeNavigate } from '../utils/navigationUtils';
import { getApiUrl } from '../utils/apiConfig';
import './Pages.css';

const LoginPage = () => {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoMessage, setSsoMessage] = useState('');
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // SSO Token Detection and Handling
  useEffect(() => {
    const handleSSOLogin = async () => {
      const ssoToken = searchParams.get('sso_token');
      const source = searchParams.get('source');
      
      if (ssoToken && source === 'wordpress') {
        console.log('SSO Token detected, attempting auto-login...');
        setSsoLoading(true);
        setSsoMessage('Auto-login from Website...');
        
        try {
          // Verify token with Website
          console.log('Verifying SSO token with Website...');
          const response = await fetch('https://dnd-map-explorer.com/wp-json/dnd-sso/v1/verify-token', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              token: ssoToken
            })
          });
          
          const result = await response.json();
          
          if (result.success && result.user) {
            console.log('SSO verification successful, logging in user:', result.user.email);
            setSsoMessage('Verification successful, logging in...');
            
            // Attempt SSO login using dedicated endpoint (no password required)
            console.log('Calling SSO login endpoint...');
            const apiUrl = getApiUrl();
            const ssoLoginResponse = await fetch(`${apiUrl}/api/auth/sso-login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: result.user.email,
                sso_verified: true
              })
            });
            
            if (ssoLoginResponse.ok) {
              const authData = await ssoLoginResponse.json();
              console.log('SSO login successful!', authData);
              setSsoMessage('Login successful! Redirecting...');
              
              // Save token to localStorage (same as regular login)
              localStorage.setItem('token', authData.token);
              
              // Set authorization header for all future requests
              const axios = (await import('axios')).default;
              axios.defaults.headers.common['Authorization'] = `Bearer ${authData.token}`;
              
              // Update auth context manually (since we're not using the login function)
              // We'll trigger a page refresh to let AuthContext pick up the token
              
              // Clean up URL parameters
              window.history.replaceState({}, document.title, '/login');
              
              // Redirect to main app
              setTimeout(() => {
                window.location.href = '/app';  // Full refresh to trigger auth context
              }, 1000);
            } else {
              const errorData = await ssoLoginResponse.json();
              console.error('SSO login failed:', errorData);
              throw new Error(errorData.message || 'SSO authentication failed');
            }
            
          } else {
            console.error('SSO verification failed:', result);
            setSsoMessage('');
            setSsoLoading(false);
            
            // Show user-friendly error message
            const errorMsg = result.message || 'Auto-login failed. Please log in manually.';
            if (window.confirm(`${errorMsg}\n\nWould you like to try again or log in manually?`)) {
              // Redirect back to Website
              window.location.href = 'https://dnd-map-explorer.com/my-account/dnd-subscriptions/';
            } else {
              // Clean up URL and allow manual login
              window.history.replaceState({}, document.title, '/login');
            }
          }
          
        } catch (error) {
          console.error('SSO Error:', error);
          setSsoMessage('');
          setSsoLoading(false);
          
          // Show user-friendly error message
          if (window.confirm('Connection error during auto-login. Please try again or log in manually.\n\nWould you like to try again?')) {
            // Redirect back to Website
            window.location.href = 'https://dnd-map-explorer.com/my-account/dnd-subscriptions/';
          } else {
            // Clean up URL and allow manual login
            window.history.replaceState({}, document.title, '/login');
          }
        }
      }
    };

    // Run SSO handling
    handleSSOLogin();
  }, [searchParams, login, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailOrUsername || !password) return;

    setIsLoading(true);
    const success = await login(emailOrUsername, password);
    setIsLoading(false);

    if (success) {
      safeNavigate(navigate, '/app');
    }
  };

  // Show SSO loading state if auto-login is in progress
  if (ssoLoading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <h2>Auto-Login from Website</h2>
          <div className="sso-loading">
            <div className="spinner"></div>
            <p>{ssoMessage}</p>
            <div className="sso-info">
              <small>Please wait while we verify your Website session...</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <h2>Login to D&D Map Explorer</h2>
        
        {error && <div className="error-message">{error}</div>}
        {ssoMessage && <div className="info-message">{ssoMessage}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Email or Username</label>
            <input
              type="text"
              id="username"
              placeholder="Enter your email or username"
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          
          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        <div className="register-link">
          <p>Don't have an account?</p>
          <a 
              href="https://dnd-map-explorer.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="register-button"
          >
            Register
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 